import { GraphQLResult, generateClient } from "aws-amplify/api";
import { AwsItems, FieldInstanceForResolver, FieldTemplate, FieldTemplateForResolver } from "db/definitions";
// @ts-ignore
import { listFieldTemplatesOffline } from "../../graphql/queries.js";
import { db } from "../db";
import { getFieldContinuationDataDocument } from "./fieldContinuationDataQueries";
import { bulkRetrieveOptionsByFieldTemplateId } from "./optionQueries";
import { recordSentry } from "utils/recordSentry";

/**
 * Given a fieldTemplate retrieved from indexedDb, finds the children properties and attaches everything, returning it. It builds it out like the amplify calls return them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {Object} fieldTemplate The field template we need to build out
 * @returns The completely finished fieldTemplate
 */
export async function buildFieldTemplate(fieldTemplate: FieldTemplateForResolver): Promise<FieldTemplate> {
	// We need to build a fieldTemplate completely here
	const [options, tableFields, continuationData] = await Promise.all([
		bulkRetrieveOptionsByFieldTemplateId(fieldTemplate.id),
		bulkRetrieveFieldTemplatesByTableField(fieldTemplate.id),
		fieldTemplate.continuationDataId ? getFieldContinuationDataDocument(fieldTemplate.continuationDataId) : null,
	]);

	// Step 5: Create promise array for potential tableFields
	const tableFieldPromises = [];
	if (tableFields.length > 0) {
		// Step 6: If a tableField has tableFields (nested tables), recurse over this function to build those out
		for (let i = 0; i < tableFields.length; i++) {
			tableFieldPromises.push(buildFieldTemplate(tableFields[i]));
		}
	}

	// Step 7: Await all promises in step 5's array
	const builtTableFields = await Promise.all(tableFieldPromises);

	// Step 8: build out the entire field with all nested stuff and return
	const fieldToReturn: FieldTemplate = { ...fieldTemplate, options: { items: options }, tableFields: { items: builtTableFields } };
	if (continuationData) {
		fieldToReturn.continuationData = continuationData;
	}
	return fieldToReturn;
}

/**
 * Given a raw fieldInstance, retrieves the fieldTemplate, builds it, and returns it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param fieldInstance The raw fieldInstance
 * @returns An array of built tableFields
 */
export async function buildFieldTemplateAsync(fieldInstance: FieldInstanceForResolver): Promise<FieldTemplate | null> {
	// Given a raw fieldInstance, retrieves the fieldTemplate, builds it, and returns it
	// Step 1: Check if there's a fieldTemplate id. If not, return null (altInstance)
	// Step 2: Get the raw fieldTemplate
	// Step 3: Build the fieldTemplate
	// Step 4: Return the fieldTemplate

	// Step 1: Check if there's a fieldTemplate id. If not, return null (altInstances for example don't have one, and that's okay)
	if (!fieldInstance.fieldTemplateId) return null;
	// Step 2: Get the raw fieldTemplate
	const retrievedTemplate = await getFieldTemplateById(fieldInstance.fieldTemplateId);

	if (retrievedTemplate) {
		// Step 3: Build the fieldTemplate
		const builtFieldTemplate = await buildFieldTemplate(retrievedTemplate);

		// Step 4: Return the fieldTemplate
		return builtFieldTemplate;
	}
	return null;
}

/**
 * Gets all fieldTemplates whose parent formTemplate id equals the provided id
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplateId The formTemplate id
 * @returns All fieldTemplates whose formTemplate === the provided formTemplateId
 */
export async function bulkRetrieveFieldTemplatesByFormTemplate(formTemplateId: string): Promise<FieldTemplateForResolver[]> {
	const retrievedFieldTemplates = await db.fieldTemplate.where("formTemplate").equals(formTemplateId).toArray();
	return retrievedFieldTemplates;
}

/**
 * Gets all fieldTemplates whose parent formTemplate id equals the provided id
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplateId The formTemplate id
 * @returns All fieldTemplates whose formTemplate === the provided formTemplateId
 */
export async function bulkRetrieveFieldTemplatesByTableField(tableField: string): Promise<FieldTemplateForResolver[]> {
	const retrievedFieldTemplates = await db.fieldTemplate.where("tableField").equals(tableField).toArray();
	return retrievedFieldTemplates;
}

/**
 * Creates list of field templates in the offline db
 * @param fieldTemplatesToCreate fieldTemplates that need to be created or updated in the db.
 * @return true or an error.
 */
export async function bulkSaveFieldTemplates(fieldTemplatesToCreate: FieldTemplateForResolver[]): Promise<void> {
	try {
		await db.fieldTemplate.bulkPut(fieldTemplatesToCreate);
	} catch (error) {
		recordSentry({ error, msg: "Error saving field templates to offline db: " });
	}
}

export function createFieldTemplateForBatchResolver(fieldTemplate: FieldTemplate): FieldTemplateForResolver {
	const fieldTemplateForResolver: FieldTemplateForResolver = {
		id: fieldTemplate.id,
		formTemplate: fieldTemplate.formTemplate,
		inputType: fieldTemplate.inputType || "text",
		exportType: fieldTemplate.exportType || "text",
		exportActions: fieldTemplate.exportActions ? [...fieldTemplate.exportActions] : [],
		header: fieldTemplate.header || "",
		action: fieldTemplate.action ? [...fieldTemplate.action] : [],
		actionData: fieldTemplate.actionData ? [...fieldTemplate.actionData] : [],
		rowOrder: fieldTemplate.rowOrder ? [...fieldTemplate.rowOrder] : [],
		deleted: fieldTemplate.deleted || false,
		optionsOrder: fieldTemplate.optionsOrder ? [...fieldTemplate.optionsOrder] : [],
		exportData: fieldTemplate.exportData ? [...fieldTemplate.exportData] : [],
		docId: fieldTemplate.docId || "",
		placeholder: fieldTemplate.placeholder || "",
		pixelFollowing: fieldTemplate.pixelFollowing || 0,
		pixelInitial: fieldTemplate.pixelInitial || 0,
		rowsAllowed: fieldTemplate.rowsAllowed || 0,
		totalExtraLines: fieldTemplate.totalExtraLines || 0,
		font: fieldTemplate.font || "",
		fontSize: fieldTemplate.fontSize || 0,
		bold: fieldTemplate.bold || false,
		createdAt: fieldTemplate.createdAt || new Date().toISOString(),
		updatedAt: fieldTemplate.updatedAt || new Date().toISOString(),
		group: fieldTemplate.group,
		csvKey: fieldTemplate.csvKey || "",
		identityRole: fieldTemplate.identityRole || "",
		readGroups: [...fieldTemplate.readGroups],
	};
	if (fieldTemplate.tableField) fieldTemplateForResolver.tableField = fieldTemplate.tableField;
	if (fieldTemplate.continuationDataId) fieldTemplateForResolver.continuationDataId = fieldTemplate.continuationDataId;
	if (fieldTemplate.rollLinkId) fieldTemplateForResolver.rollLinkId = fieldTemplate.rollLinkId;
	if (fieldTemplate.baseFieldTemplate) fieldTemplateForResolver.baseFieldTemplate = fieldTemplate.baseFieldTemplate;
	if (fieldTemplate.version) fieldTemplateForResolver.version = fieldTemplate.version;
	return fieldTemplateForResolver;
}

export function createMergedFieldTemplateForBatchResolver(
	donorFieldTemplate: FieldTemplateForResolver,
	receiverFieldTemplate: FieldTemplateForResolver,
): FieldTemplateForResolver {
	// LISTEN UP: To be in here, you should already have the correct formTemplate selected, and by that, get the fieldTemplates from it. This should only be used in the event that both
	// parties have the fieldTemplate with the same id, and we just want to see if the updatedAt time is different so we can take that version
	const donorUpdatedAt = new Date(donorFieldTemplate.updatedAt);
	const receiverUpdatedAt = new Date(receiverFieldTemplate.updatedAt);
	const timeStamp = new Date();
	// Saves repeated calculations, even if they're minor
	const evaluation = donorUpdatedAt > receiverUpdatedAt;
	const finalFieldTemplate: FieldTemplateForResolver = {
		id: donorFieldTemplate.id || receiverFieldTemplate.id,
		action: (evaluation ? donorFieldTemplate.action : receiverFieldTemplate.action) || [],
		actionData: (evaluation ? donorFieldTemplate.actionData : receiverFieldTemplate.actionData) || [],
		bold: (evaluation ? donorFieldTemplate.bold : receiverFieldTemplate.bold) || false,
		deleted: (evaluation ? donorFieldTemplate.deleted : receiverFieldTemplate.deleted) || false,
		docId: (evaluation ? donorFieldTemplate.docId : receiverFieldTemplate.docId) || "",
		exportActions: (evaluation ? donorFieldTemplate.exportActions : receiverFieldTemplate.exportActions) || [],
		exportData: (evaluation ? donorFieldTemplate.exportData : receiverFieldTemplate.exportData) || [],
		exportType: (evaluation ? donorFieldTemplate.exportType : receiverFieldTemplate.exportType) || "",
		font: (evaluation ? donorFieldTemplate.font : receiverFieldTemplate.font) || "",
		fontSize: (evaluation ? donorFieldTemplate.fontSize : receiverFieldTemplate.fontSize) || 0,
		formTemplate: (evaluation ? donorFieldTemplate.formTemplate : receiverFieldTemplate.formTemplate) || "",
		header: (evaluation ? donorFieldTemplate.header : receiverFieldTemplate.header) || "",
		inputType: (evaluation ? donorFieldTemplate.inputType : receiverFieldTemplate.inputType) || "",
		optionsOrder: (evaluation ? donorFieldTemplate.optionsOrder : receiverFieldTemplate.optionsOrder) || [],
		pixelFollowing: (evaluation ? donorFieldTemplate.pixelFollowing : receiverFieldTemplate.pixelFollowing) || 0,
		placeholder: (evaluation ? donorFieldTemplate.placeholder : receiverFieldTemplate.placeholder) || "",
		pixelInitial: (evaluation ? donorFieldTemplate.pixelInitial : receiverFieldTemplate.pixelInitial) || 0,
		rowsAllowed: (evaluation ? donorFieldTemplate.rowsAllowed : receiverFieldTemplate.rowsAllowed) || 0,
		rowOrder: (evaluation ? donorFieldTemplate.rowOrder : receiverFieldTemplate.rowOrder) || [],
		totalExtraLines: (evaluation ? donorFieldTemplate.totalExtraLines : receiverFieldTemplate.totalExtraLines) || 0,
		version: (evaluation ? donorFieldTemplate.version : receiverFieldTemplate.version) || "",
		createdAt: (evaluation ? donorFieldTemplate.createdAt : receiverFieldTemplate.createdAt) || timeStamp.toISOString(),
		updatedAt: (evaluation ? donorFieldTemplate.updatedAt : receiverFieldTemplate.updatedAt) || timeStamp.toISOString(),
		group: evaluation ? donorFieldTemplate.group : receiverFieldTemplate.group,
		readGroups: evaluation ? [...donorFieldTemplate.readGroups] : [...receiverFieldTemplate.readGroups],
		csvKey: (evaluation ? donorFieldTemplate.csvKey : receiverFieldTemplate.csvKey) || "",
		identityRole: (evaluation ? donorFieldTemplate.identityRole : receiverFieldTemplate.identityRole) || "",
	};

	if (evaluation) {
		if (donorFieldTemplate.tableField) finalFieldTemplate.tableField = donorFieldTemplate.tableField;
		if (donorFieldTemplate.rollLinkId) finalFieldTemplate.rollLinkId = donorFieldTemplate.rollLinkId;
		if (donorFieldTemplate.continuationDataId) finalFieldTemplate.continuationDataId = donorFieldTemplate.continuationDataId;
		if (donorFieldTemplate.baseFieldTemplate) finalFieldTemplate.baseFieldTemplate = donorFieldTemplate.baseFieldTemplate;
	} else {
		if (receiverFieldTemplate.tableField) finalFieldTemplate.tableField = receiverFieldTemplate.tableField;
		if (receiverFieldTemplate.rollLinkId) finalFieldTemplate.rollLinkId = receiverFieldTemplate.rollLinkId;
		if (receiverFieldTemplate.continuationDataId) finalFieldTemplate.continuationDataId = receiverFieldTemplate.continuationDataId;
		if (receiverFieldTemplate.baseFieldTemplate) finalFieldTemplate.baseFieldTemplate = receiverFieldTemplate.baseFieldTemplate;
	}
	return finalFieldTemplate;
}

/**
 * Given the parent formTemplate id, deletes all fieldTemplates that are children
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplateId The id of the parent formTemplate
 */
export async function deleteFieldTemplatesByFormTemplate(formTemplateId: string): Promise<void> {
	await db.fieldTemplate.where("formTemplate").equals(formTemplateId).delete();
}

/**
 * Queries the count of all FieldTemplates, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all fieldTemplates
 */
export async function getTotalFieldTemplateDiskSize() {
	const number = await db.fieldTemplate.count();
	const AVERAGE_DOCUMENT_SIZE = 391.5;
	return number * AVERAGE_DOCUMENT_SIZE;
}

/**
 * Given a fieldTemplate id, grabs the fieldTemplate with that id from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the fieldTempalte
 * @returns The fieldTemplate with that id, or undefined if one wasn't found
 */
async function getFieldTemplateById(id: string): Promise<FieldTemplateForResolver | undefined> {
	const fieldTemplate = await db.fieldTemplate.get(id);
	return fieldTemplate;
}

interface FieldTemplateResponse {
	listFieldTemplates: AwsItems<FieldTemplateForResolver>;
}
/**
 * Recursively goes and grabs all fieldTemplates to be stored offline
 * @author Eric Webb <ewebb@factorearth.com>
 * @param previousItems The previously retrieved fieldTemplates
 * @param nextToken The nextToken to continue fetching the fieldTemplates
 * @returns All of the fieldTemplates
 */
export async function getFieldTemplatesRecursive(
	previousItems: FieldTemplateForResolver[] | undefined = undefined,
	nextToken: string | undefined = undefined,
): Promise<FieldTemplateForResolver[]> {
	try {
		const client = generateClient({ authMode: "userPool" });
		const response = (await client.graphql({
			authMode: "userPool",
			query: listFieldTemplatesOffline,
			variables: { nextToken },
		})) as GraphQLResult<FieldTemplateResponse>;
		const innerStuff = response.data.listFieldTemplates;
		if (!previousItems) {
			previousItems = innerStuff.items;
		} else {
			previousItems = previousItems.concat(innerStuff.items);
		}
		if (innerStuff.nextToken) return getFieldTemplatesRecursive(previousItems, innerStuff.nextToken);
		return previousItems;
	} catch (err) {
		recordSentry({ error: err, msg: "Error fetching field templates for offline: " });
	}
	return [];
}

/**
 * Bulk deletes field templates from the offline db.
 * @param fieldTemplates FieldTemplates that need to be deleted.
 */
export async function bulkDeleteFieldTemplates(fieldTemplates: FieldTemplate[]) {
	try {
		const fieldTemplateIds = fieldTemplates.map((template) => template.id);
		await db.fieldTemplate.bulkDelete(fieldTemplateIds);
	} catch (err) {
		recordSentry({ error: err, msg: "Error batch deleting fieldTemplates in offline db" });
	}
}
